import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { naira } from '@/lib/format';
import { getWalletBanks, postWalletResolveAccount, postWalletTransfer, postWalletTransferWallet, walletError } from '@/api/parent';
import { WalletPinAction } from './WalletPinAction';
import { BankPicker, Sheet, ghost, input, primary } from './ui';

interface Props {
  mode: 'bank' | 'wallet';
  balance: number;
  onClose: () => void;
  onDone: () => void;
  onNeedWallet: () => void;
  onNeedPin: () => void;
}

/** Send money from the parent wallet to any Nigerian bank account, or to another OJP wallet. OJP's charge is shown before the PIN. */
export function SendDialog({ mode, balance, onClose, onDone, onNeedWallet, onNeedPin }: Props) {
  const [bankCode, setBankCode] = useState('');
  const [account, setAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [narration, setNarration] = useState('');
  const [resolved, setResolved] = useState('');
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');
  const [review, setReview] = useState(false);

  const banks = useQuery({ queryKey: ['parent-wallet-banks'], queryFn: getWalletBanks, enabled: mode === 'bank', staleTime: 3_600_000 });
  const bank = banks.data?.find((b) => b.code === bankCode);
  const num = Number(amount);
  const accountOk = mode === 'bank' ? /^\d{10}$/.test(account) : /^\d{10}$/.test(account);

  useEffect(() => {
    setResolved(''); setError('');
    if (mode !== 'bank' || !bankCode || !/^\d{10}$/.test(account)) return;
    let live = true;
    setResolving(true);
    postWalletResolveAccount({ bank_code: bankCode, account_number: account })
      .then((r) => live && setResolved(r.account_name))
      .catch((e) => live && setError(walletError(e).message))
      .finally(() => live && setResolving(false));
    return () => { live = false; };
  }, [mode, bankCode, account]);

  if (review) {
    const details = mode === 'bank'
      ? [{ label: 'To', value: resolved }, { label: 'Bank', value: bank?.name ?? '' }, { label: 'Account number', value: account }, ...(narration ? [{ label: 'Narration', value: narration }] : [])]
      : [{ label: 'To wallet', value: account }];
    return (
      <WalletPinAction
        title={mode === 'bank' ? 'Send to bank' : 'Send to another wallet'}
        quoteType={mode === 'bank' ? 'bank_transfer' : 'wallet_transfer'}
        amount={num}
        balance={balance}
        details={details}
        onCancel={() => setReview(false)}
        onFinished={onDone}
        onNeedWallet={onNeedWallet}
        onNeedPin={onNeedPin}
        run={async (pin, key) => {
          if (mode === 'bank') {
            const r = await postWalletTransfer({ amount: num, bank_code: bankCode, account_number: account, bank_name: bank?.name, narration: narration || undefined }, pin, key);
            return `${naira(num)} sent to ${r.account_name ?? resolved}. New balance: ${naira(r.wallet_balance)}.`;
          }
          const r = await postWalletTransferWallet({ amount: num, account_number: account }, pin, key);
          return `${naira(r.amount)} sent to ${r.to_account_name}. New balance: ${naira(r.wallet_balance)}.`;
        }}
      />
    );
  }

  const ready = accountOk && num >= 100 && num <= balance && (mode === 'wallet' || (!!bankCode && !!resolved));
  return (
    <Sheet title={mode === 'bank' ? 'Send to bank' : 'Send to another wallet'} sub={`Available: ${naira(balance)}`} onClose={onClose}
      footer={<><button onClick={onClose} className={ghost}>Cancel</button><button onClick={() => setReview(true)} disabled={!ready} className={primary}>Continue</button></>}>
      <div className="space-y-4">
        {mode === 'bank' && (
          <div><p className="text-sm font-semibold text-slate-700 mb-1.5">Bank</p>
            <BankPicker banks={banks.data ?? []} loading={banks.isLoading} value={bankCode} onChange={(c) => { setBankCode(c); setResolved(''); }} />
            {banks.isError && <p className="text-sm text-red-600 mt-1">Could not load banks. Close and try again.</p>}
          </div>
        )}
        <label className="block"><span className="text-sm font-semibold text-slate-700">{mode === 'bank' ? 'Account number' : 'Their OJP wallet account number'}</span>
          <input className={`${input} mt-1 font-mono tracking-wider`} inputMode="numeric" maxLength={10} placeholder="10 digits" value={account} onChange={(e) => setAccount(e.target.value.replace(/\D/g, '').slice(0, 10))} />
        </label>
        {mode === 'bank' && resolving && <p className="text-sm text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin -mt-0.5 mr-1" />Checking the account name…</p>}
        {resolved && <p className="text-sm font-semibold text-green-800 bg-green-50 border border-green-200 rounded-xl px-3 py-2"><CheckCircle2 className="w-4 h-4 inline -mt-0.5 mr-1" />{resolved}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <label className="block"><span className="text-sm font-semibold text-slate-700">Amount</span>
          <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₦</span>
            <input className={`${input} pl-7 text-lg font-semibold`} inputMode="decimal" placeholder="Minimum ₦100" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} /></div>
          {num > balance && <span className="text-xs text-red-600">More than your balance.</span>}
        </label>
        {mode === 'bank' && (
          <label className="block"><span className="text-sm font-semibold text-slate-700">Narration <span className="font-normal text-slate-400">(optional)</span></span>
            <input className={`${input} mt-1`} maxLength={60} placeholder="e.g. Uniform" value={narration} onChange={(e) => setNarration(e.target.value)} /></label>
        )}
        <p className="text-xs text-slate-500">You'll see OJP's charge on the next screen before you enter your PIN.</p>
      </div>
    </Sheet>
  );
}
